import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useCurrency } from '../lib/currency';
import { useSession } from '../lib/session';

/**
 * Route identifiers and their handlers live in one list.
 *
 * §11 calls out "tab/route identifiers must match their handlers" as a known
 * trap. Deriving both the link and the label from a single array is what stops
 * a renamed route from silently pointing at nothing.
 */
export const ROUTES = [
  { path: '/', label: 'Summary' },
  { path: '/holdings', label: 'Holdings' },
  { path: '/goals', label: 'Goals' },
  { path: '/calculators', label: 'Calculators' },
  { path: '/insights', label: 'Insights' },
] as const;

function CurrencyToggle() {
  const { display, canSwitch, setDisplay, rateStale } = useCurrency();

  return (
    <div
      className="flex items-center rounded-md border border-black/15 p-0.5 dark:border-white/15"
      role="group"
      aria-label="Display currency"
    >
      {(['INR', 'USD'] as const).map((currency) => (
        <button
          key={currency}
          type="button"
          onClick={() => setDisplay(currency)}
          disabled={currency === 'USD' && !canSwitch}
          title={
            currency === 'USD' && !canSwitch
              ? 'The USD/INR rate is unavailable, so figures cannot be converted right now.'
              : rateStale && currency === 'USD'
                ? 'Converted at the last known USD/INR rate.'
                : undefined
          }
          aria-pressed={display === currency}
          className={`rounded px-2 py-1 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
            display === currency
              ? 'bg-brand text-white'
              : 'text-ink-muted hover:bg-black/5 dark:text-ink-inverse-muted dark:hover:bg-white/5'
          }`}
        >
          {currency}
          {currency === 'USD' && rateStale && display === 'USD' && (
            <span aria-hidden="true" title="Rate may be out of date">
              {' '}
              ·
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export default function Nav() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-md px-3 py-2 text-sm font-medium ${
      isActive
        ? 'bg-brand-soft text-brand-strong dark:bg-brand/20 dark:text-brand-soft'
        : 'text-ink-muted hover:bg-black/5 dark:text-ink-inverse-muted dark:hover:bg-white/5'
    }`;

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <header className="sticky top-0 z-20 border-b border-black/10 bg-surface/95 backdrop-blur dark:border-white/10 dark:bg-surface-dark/95">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-2 px-3 py-2 sm:px-4">
        <NavLink to="/" className="mr-1 flex items-center gap-2 font-semibold">
          <span aria-hidden="true">🔥</span>
          <span className="hidden sm:inline">FIRE Tracker</span>
        </NavLink>

        <nav aria-label="Main" className="hidden flex-1 items-center gap-1 md:flex">
          {ROUTES.map((route) => (
            <NavLink key={route.path} to={route.path} end={route.path === '/'} className={linkClass}>
              {route.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <CurrencyToggle />
          <NavLink to="/settings" className={linkClass} title={user?.email}>
            <span className="hidden sm:inline">Settings</span>
            <span className="sm:hidden" aria-hidden="true">
              ⚙
            </span>
            <span className="sr-only sm:hidden">Settings</span>
          </NavLink>
          <button type="button" className="btn-secondary hidden sm:inline-flex" onClick={handleSignOut}>
            Sign out
          </button>
          <button
            type="button"
            className="btn-secondary md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            <span className="sr-only">Menu</span>
            <span aria-hidden="true">☰</span>
          </button>
        </div>
      </div>

      {open && (
        <nav
          id="mobile-nav"
          aria-label="Main"
          className="border-t border-black/10 px-3 py-2 md:hidden dark:border-white/10"
        >
          <ul className="flex flex-col gap-1">
            {ROUTES.map((route) => (
              <li key={route.path}>
                <NavLink
                  to={route.path}
                  end={route.path === '/'}
                  className={linkClass}
                  onClick={() => setOpen(false)}
                >
                  {route.label}
                </NavLink>
              </li>
            ))}
            <li>
              <button type="button" className="btn-secondary mt-1 w-full" onClick={handleSignOut}>
                Sign out
              </button>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}
